import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import { cn } from '../lib/utils';
import { Language } from '../types';

interface StalkerProgressChartsProps {
  chartData: any[];
  goals: any;
  lang: Language;
  history?: any[];
  period?: string;
  weightTrendData?: any;
}

export const StalkerProgressCharts: React.FC<StalkerProgressChartsProps> = ({ 
  chartData, 
  goals, 
  lang, 
  history = [], 
  period = 'daily',
  weightTrendData
}) => {
  const [glitch, setGlitch] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.8) {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 150);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Use the latest day's data for current stats (Macro Breakdown and Micro Nutrients)
  const currentData = chartData[chartData.length - 1] || {
    protein: 0, carbs: 0, fat: 0, calories: 0, fiber: 0, sugar: 0, sodium: 0
  };

  const calcPercentage = (val: number, target: number) => Math.min(100, Math.max(0, (val / target) * 100));

  // 4-hour window meal timing calculation
  const getMealTimingData = () => {
    const windows = [
      { name: lang === 'ua' ? '00:00 - 04:00' : '12 AM - 4 AM', count: 0, label: lang === 'ua' ? 'Ніч' : 'Night' },
      { name: lang === 'ua' ? '04:00 - 08:00' : '4 AM - 8 AM', count: 0, label: lang === 'ua' ? 'Ранок' : 'Morning' },
      { name: lang === 'ua' ? '08:00 - 12:00' : '8 AM - 12 PM', count: 0, label: lang === 'ua' ? 'День' : 'Midday' },
      { name: lang === 'ua' ? '12:00 - 16:00' : '12 PM - 4 PM', count: 0, label: lang === 'ua' ? 'Обід' : 'Afternoon' },
      { name: lang === 'ua' ? '16:00 - 20:00' : '4 PM - 8 PM', count: 0, label: lang === 'ua' ? 'Вечір' : 'Evening' },
      { name: lang === 'ua' ? '20:00 - 00:00' : '8 PM - 12 AM', count: 0, label: lang === 'ua' ? 'Пізній вечір' : 'Late Night' },
    ];

    if (!history || history.length === 0) return windows;

    const now = new Date();
    let filteredEntries = [...history];

    if (period === 'daily') {
      const limitDate = new Date(now);
      limitDate.setDate(limitDate.getDate() - 7);
      filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    } else if (period === 'weekly') {
      const limitDate = new Date(now);
      limitDate.setDate(limitDate.getDate() - 28);
      filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    } else if (period === 'monthly') {
      const limitDate = new Date(now);
      limitDate.setMonth(limitDate.getMonth() - 6);
      filteredEntries = history.filter(e => new Date(e.timestamp) >= limitDate);
    }

    filteredEntries.forEach(entry => {
      const date = new Date(entry.timestamp);
      const hour = date.getHours();
      const windowIdx = Math.floor(hour / 4) % 6;
      windows[windowIdx].count += 1;
    });

    return windows;
  };

  const mealTimingData = getMealTimingData();
  const totalMealsLogged = mealTimingData.reduce((sum, w) => sum + w.count, 0);
  const peakWindow = mealTimingData.reduce((max, w) => (w.count > (max?.count || 0) ? w : max), mealTimingData[0]);
  const displayPeakWindow = totalMealsLogged > 0 ? peakWindow : null;

  const safeGoals = goals || {
    calories: 2000,
    protein: 100,
    carbs: 250,
    fat: 70,
    water_ml: 2000,
    fiber_g: 30,
    sugar_g: 50,
    sodium_mg: 2300
  };

  const proteinPct = calcPercentage(currentData.protein, safeGoals.protein || 1);
  const carbsPct = calcPercentage(currentData.carbs, safeGoals.carbs || 1);
  const fatPct = calcPercentage(currentData.fat, safeGoals.fat || 1);
  const waterPct = calcPercentage(currentData.water || 0, safeGoals.water_ml || 2000);

  // Calorie EKG Data (Modify actual data to look jagged and erratic)
  const ekgData = chartData.map((d) => ({
    name: d.name,
    calories: d.calories,
    // Add some noise for the 'anomaly' look
    erratic: d.calories + (Math.random() * 200 - 100),
  }));

  const lastCalorie = currentData.calories;
  const lastWater = currentData.water || 0;
  
  const isCriticalCalorie = lastCalorie < safeGoals.calories * 0.5;
  const isCriticalWater = lastWater < (safeGoals.water_ml || 2000) * 0.4;
  
  const calPercent = lastCalorie / safeGoals.calories;
  const waterPercent = lastWater / (safeGoals.water_ml || 2000);
  
  let aiFeedback = "";
  let isAlert = false;
  
  if (isCriticalCalorie || isCriticalWater) {
    aiFeedback = lang === 'ua' 
      ? "УВАГА: КРИТИЧНИЙ РІВЕНЬ ГОЛОДУ. ПРОТОКОЛИ ОЧИЩЕННЯ ВІД РАДІАЦІЇ ДАЮТЬ ЗБІЙ."
      : "ALERT: HUNGER CRITICAL. RADIATION SCRUBBING PROTOCOLS FAILING.";
    isAlert = true;
  } else if (calPercent > 1.15) {
    aiFeedback = lang === 'ua'
      ? "УВАГА: НАДЛИШОК ПАЛИВА. ЗАЙВА МАСА СПОВІЛЬНИТЬ БІГ."
      : "ALERT: OVER-FUELING. EXCESS MASS WILL SLOW SPRINT.";
    isAlert = true;
  } else if (calPercent >= 0.9 && calPercent <= 1.1) {
    aiFeedback = lang === 'ua'
      ? "СТАТУС: ПОКАЗНИКИ СИЛИ ПІДВИЩЕНО. ГОТОВИЙ ДО ПОШУКУ АРТЕФАКТІВ."
      : "STATUS: STRENGTH STATS BOOSTED. READY FOR ARTIFACT SCAVENGING.";
  } else {
    aiFeedback = lang === 'ua'
      ? "СТАТУС: СИНХРОНІЗАЦІЯ. ПРОДОВЖУЙ РУХ."
      : "STATUS: SYNCING. KEEP MOVING.";
  }

  return (
    <div 
      id="daily-streak-section"
      className={cn(
        "space-y-4 px-2 pb-10 text-[#4ade80] font-mono scroll-mt-24",
        glitch ? "translate-x-1" : ""
      )}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=VT323&display=swap');
        .stalker-font { font-family: 'VT323', monospace; letter-spacing: 0.05em; }
        .text-glow { text-shadow: 0 0 8px rgba(74, 222, 128, 0.8); }
        .text-glow-orange { text-shadow: 0 0 8px rgba(249, 115, 22, 0.8); }
        .text-glow-red { text-shadow: 0 0 8px rgba(220, 38, 38, 0.8); }
        .text-glow-cyan { text-shadow: 0 0 8px rgba(34, 211, 238, 0.8); }
        
        .stalker-panel {
          background: rgba(10, 10, 10, 0.85);
          border: 1px solid rgba(74, 222, 128, 0.3);
          border-radius: 4px;
          box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.8), 0 0 5px rgba(74, 222, 128, 0.1);
        }
      `}</style>
      
      {/* Header */}
      <div className="flex flex-col items-center mb-6">
        <h2 className="text-xl sm:text-2xl stalker-font text-glow text-[#4ade80] mb-2 uppercase text-center border-b border-[#4ade80]/30 pb-1 w-full border-dashed">
          ZONE VITALS & SCANS [LIVE-LINK]
        </h2>
        <div className="flex gap-2 w-full mt-2">
          <button className="flex-1 stalker-font border border-[#4ade80]/50 text-[#4ade80] bg-[#4ade80]/10 hover:bg-[#4ade80]/20 py-1 text-center uppercase shadow-[0_0_5px_rgba(74,222,128,0.3)]">
            STALK CYCLE
          </button>
          <button className="flex-1 stalker-font border border-[#4ade80]/30 text-[#4ade80]/60 bg-transparent hover:bg-[#4ade80]/10 py-1 text-center uppercase">
            DEEP SCAN
          </button>
        </div>
      </div>

      {/* BIOMETRIC STABILITY DATA */}
      <div className="stalker-panel p-3">
        <h3 className="text-lg stalker-font text-glow text-[#4ade80] mb-4">BIOMETRIC STABILITY DATA</h3>
        
        <div className="flex gap-4 sm:gap-6 justify-between items-start">
          {/* Vials */}
          <div className="flex gap-2 sm:gap-4 flex-1">
            <Vial label="CARB" pct={carbsPct} color="orange" />
            <Vial label="FAT" pct={fatPct} color="red" />
            <Vial label="PROTEIN" pct={proteinPct} color="green" />
            <Vial label="WATER" pct={waterPct} color="green" />
          </div>

          {/* Geiger Counter & Status */}
          <div className="w-[120px] sm:w-[150px] flex flex-col items-center border-l border-[#4ade80]/20 pl-4 border-dashed">
            <div className="relative w-full h-[60px] overflow-hidden mb-2">
              {/* Dial Background Arc */}
              <div className="absolute bottom-0 left-0 right-0 h-[100px] rounded-t-full border-t border-l border-r border-[#4ade80]/50" />
              
              {/* Scale Marks */}
              <div className="absolute inset-0">
                {[0, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20].map((val, i) => (
                  <div key={i} className="absolute bottom-0 left-1/2 w-[1px] h-[5px] bg-[#4ade80] origin-bottom" style={{ transform: `translateX(-50%) rotate(${(i - 4) * 15}deg) translateY(-50px)` }} />
                ))}
              </div>
              
              {/* Moving Needle */}
              <motion.div 
                className="absolute bottom-0 left-1/2 w-[2px] h-[60px] bg-amber-500 origin-bottom z-10"
                style={{ marginLeft: '-1px' }}
                animate={{ rotate: [-30, -25, -40, -10, -20, 10, -15, 30, -30] }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
              />
            </div>
            <div className="stalker-font text-[#4ade80] text-center text-xs sm:text-sm">
              <span className="text-lg">Sv/h</span><br/>
              RADIATION SCAN
            </div>

            <div className="mt-4 stalker-font text-[10px] sm:text-xs text-[#4ade80]/80 uppercase break-words w-full">
              {aiFeedback}
            </div>
          </div>
        </div>
      </div>

      {/* SYSTEM FUEL SYNC */}
      <div className="stalker-panel p-3">
        <div className="flex justify-between items-end mb-2">
          <h3 className="text-lg stalker-font text-glow text-[#4ade80] uppercase">SYSTEM FUEL SYNC</h3>
          <div className="text-xs stalker-font text-[#4ade80]">
            SYNC TARGET: {safeGoals.calories}
          </div>
        </div>
        
        <div className="h-[120px] w-full relative border border-[#4ade80]/30 bg-[#4ade80]/5">
          {/* Target Line */}
          <div className="absolute top-[50%] left-0 right-0 h-[1px] border-t border-dashed border-amber-500/50" />
          <div className="absolute left-1 flex flex-col justify-between h-full py-1 text-[8px] sm:text-[10px] stalker-font text-[#4ade80]/50 z-10 pointer-events-none">
            <span>KCAL</span>
            <span>2500</span>
            <span>2000</span>
            <span>1500</span>
            <span>1000</span>
            <span>500</span>
            <span>0</span>
          </div>

          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ekgData} margin={{ top: 10, right: 10, left: 30, bottom: 5 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#4ade80" strokeOpacity={0.1} vertical={false} />
              <Tooltip 
                contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: '1px solid #4ade80', color: '#4ade80', fontFamily: 'VT323' }}
                itemStyle={{ color: '#4ade80' }}
              />
              <Line 
                type="step" 
                dataKey="erratic" 
                stroke="#4ade80" 
                strokeWidth={1.5} 
                dot={false} 
                activeDot={{ r: 4, fill: '#4ade80' }} 
                isAnimationActive={true}
                animationDuration={1000}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
          
          <div className="absolute bottom-2 right-2 border border-amber-500/50 bg-black/80 px-2 py-1 stalker-font text-xs sm:text-sm text-amber-500 uppercase">
            CALORIES : <br />
            [CRITICAL SYNC: {Math.round(lastCalorie)}]
          </div>
        </div>
      </div>

      {/* MEAL TIMING BREAKDOWN */}
      <div className="stalker-panel p-3">
        <div className="flex justify-between items-end mb-2">
          <h3 className="text-lg stalker-font text-glow text-[#4ade80] uppercase">
            {lang === 'ua' ? "АНАЛІЗ ЧАСУ ПРИЙОМУ ЇЖІ" : "MEAL TIMING BREAKDOWN"}
          </h3>
          <div className="text-xs stalker-font text-[#4ade80]">
            {lang === 'ua' ? "ПЕРІОД СИНХРОНІЗАЦІЇ" : "SYNC PERIOD"}: {period.toUpperCase()}
          </div>
        </div>

        <div className="h-[160px] w-full relative border border-[#4ade80]/30 bg-[#4ade80]/5 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={mealTimingData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#4ade80" strokeOpacity={0.1} vertical={false} />
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: '#4ade80', fontSize: 10, fontFamily: 'VT323' }}
              />
              <YAxis 
                dataKey="count"
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                tick={{ fill: '#4ade80', fontSize: 10, fontFamily: 'VT323' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(0,0,0,0.9)', 
                  border: '1px solid #4ade80', 
                  color: '#4ade80', 
                  fontFamily: 'VT323' 
                }}
                itemStyle={{ color: '#4ade80' }}
                cursor={{ fill: 'rgba(74, 222, 128, 0.05)' }}
              />
              <Bar 
                dataKey="count" 
                name={lang === 'ua' ? "Прийоми їжі" : "Meals"} 
                fill="#4ade80" 
                radius={[2, 2, 0, 0]}
                isAnimationActive={true}
                animationDuration={900}
                animationEasing="ease-out"
              >
                {mealTimingData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.count > 0 ? '#4ade80' : 'rgba(74, 222, 128, 0.1)'} 
                    stroke="#4ade80"
                    strokeWidth={entry.count > 0 ? 1 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs stalker-font text-[#4ade80]/80">
          <div className="border border-[#4ade80]/20 bg-black/40 p-2 rounded">
            <div>{lang === 'ua' ? "ПІК АКТИВНОСТІ ХАРЧУВАННЯ:" : "PEAK FEEDING WINDOW:"}</div>
            <div className="text-sm text-glow text-amber-500 font-bold uppercase mt-1">
              {displayPeakWindow ? `${displayPeakWindow.label} (${displayPeakWindow.name})` : "N/A"}
            </div>
          </div>
          <div className="border border-[#4ade80]/20 bg-black/40 p-2 rounded">
            <div>{lang === 'ua' ? "ЗАГ. КІЛЬКІСТЬ СИНХРОНІЗАЦІЙ:" : "TOTAL LOGGED SAMPLES:"}</div>
            <div className="text-sm text-glow text-[#4ade80] font-bold uppercase mt-1">
              {totalMealsLogged} {lang === 'ua' ? "ПРИЙОМІВ" : "MEALS"}
            </div>
          </div>
        </div>
      </div>

      {/* BIOMASS TELEMETRY / BODY WEIGHT TREND */}
      {weightTrendData && weightTrendData.data && (
        <div className="stalker-panel p-3">
          <div className="flex justify-between items-end mb-2">
            <h3 className="text-lg stalker-font text-glow text-[#4ade80] uppercase">
              {lang === 'ua' ? "БІОМАСА ОПЕРАТОРА / ДИНАМІКА ВАГИ" : "OPERATOR BIOMASS / WEIGHT TREND"}
            </h3>
            <div className="text-xs stalker-font text-amber-500">
              {lang === 'ua' ? "ПОТОЧНА МАСА" : "CURRENT MASS"}: {weightTrendData.currentWeight} KG
            </div>
          </div>

          <div className="h-[140px] w-full relative border border-[#4ade80]/30 bg-[#4ade80]/5 p-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightTrendData.data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#4ade80" strokeOpacity={0.1} vertical={false} />
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#4ade80', fontSize: 10, fontFamily: 'VT323' }}
                />
                <YAxis 
                  domain={['auto', 'auto']}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#4ade80', fontSize: 10, fontFamily: 'VT323' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(0,0,0,0.9)', 
                    border: '1px solid #4ade80', 
                    color: '#4ade80', 
                    fontFamily: 'VT323' 
                  }}
                  itemStyle={{ color: '#4ade80' }}
                  formatter={(value: any, name: any) => [
                    `${value} kg`, 
                    name === 'trend' ? (lang === 'ua' ? 'Лінія тренду' : 'Trend Line') : (lang === 'ua' ? 'Виміряна вага' : 'Measured Weight')
                  ]}
                />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  connectNulls={true}
                  stroke="#4ade80" 
                  strokeWidth={2} 
                  dot={{ r: 3, fill: '#4ade80' }}
                  activeDot={{ r: 5, fill: '#f97316' }} 
                  isAnimationActive={true}
                />
                <Line 
                  type="linear" 
                  dataKey="trend" 
                  stroke="#f97316" 
                  strokeWidth={1.5} 
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2 text-xs stalker-font text-[#4ade80]/80">
            <div className="border border-[#4ade80]/20 bg-black/40 p-2 rounded text-center">
              <div>{lang === 'ua' ? "ЗМІНА ВАГИ:" : "MASS DELTA:"}</div>
              <div className={cn(
                "text-sm font-bold uppercase mt-1",
                weightTrendData.changeKg <= 0 ? "text-[#4ade80]" : "text-amber-500"
              )}>
                {weightTrendData.changeKg > 0 ? `+${weightTrendData.changeKg}` : weightTrendData.changeKg} KG
              </div>
            </div>
            <div className="border border-[#4ade80]/20 bg-black/40 p-2 rounded text-center">
              <div>{lang === 'ua' ? "ТЕМП / ТИЖДЕНЬ:" : "WEEKLY PACE:"}</div>
              <div className="text-sm text-glow text-cyan-400 font-bold uppercase mt-1">
                {weightTrendData.weeklyRateKg > 0 ? `+${weightTrendData.weeklyRateKg}` : weightTrendData.weeklyRateKg} KG/WK
              </div>
            </div>
            <div className="border border-[#4ade80]/20 bg-black/40 p-2 rounded text-center">
              <div>{lang === 'ua' ? "СТАТУС ЗОНИ:" : "ZONE STATUS:"}</div>
              <div className="text-sm text-glow text-amber-500 font-bold uppercase mt-1">
                {weightTrendData.isLosing ? (lang === 'ua' ? "СПАЛЮВАННЯ МАСИ" : "BURNING MASS") : weightTrendData.isGaining ? (lang === 'ua' ? "НАБІР ВАГИ" : "GAINING LOAD") : (lang === 'ua' ? "СТАБІЛЬНИЙ" : "STABLE LOAD")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ALERT BOX */}
      {isAlert && (
        <div className="border border-red-600/50 bg-red-900/20 p-3 flex gap-3 items-center">
          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center border border-red-500">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 text-red-500">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="stalker-font text-red-500 text-sm sm:text-base uppercase flex-1">
            {aiFeedback}
          </div>
          <div className="w-8 h-8 flex-shrink-0 opacity-50 flex flex-col items-center justify-center">
            <div className="w-5 h-5 rounded-full border border-red-500 relative flex items-center justify-center">
               <div className="w-1 h-1 bg-red-500 rounded-full" />
               <div className="absolute w-6 h-[1px] bg-red-500 rotate-45" />
               <div className="absolute w-6 h-[1px] bg-red-500 -rotate-45" />
            </div>
            <div className="text-[6px] text-red-500 stalker-font mt-1">v.2.0.5</div>
          </div>
        </div>
      )}

      {/* OTHER NUTRIENTS SCAN */}
      <div className="stalker-panel p-3">
        <div className="flex justify-between items-end border-b border-[#4ade80]/30 pb-2 mb-3 border-dashed">
          <h3 className="text-lg stalker-font text-[#4ade80] uppercase">OTHER NUTRIENTS SCAN</h3>
          <div className="flex gap-4 text-[#4ade80]/60 stalker-font text-xs uppercase">
            <span>TARGET</span>
            <span>STATUS</span>
          </div>
        </div>
        
        <div className="space-y-3">
          <NutrientRow label="FIBER" icon="🌾" value={currentData.fiber} target={safeGoals.fiber_g} unit="g" />
          <NutrientRow label="SUGAR" icon="🧊" value={currentData.sugar * 1000} target={safeGoals.sugar_g * 1000} unit="mg" invert />
          <NutrientRow label="SODIUM" icon="⬡" value={currentData.sodium} target={safeGoals.sodium_mg} unit="mg" divisor={1} invert />
          <NutrientRow label="POTASSIUM" icon="K" value={currentData.potassium || 0} target={3500} unit="g" divisor={1000} />
          <NutrientRow label="MAGNESIUM" icon="Mg" value={currentData.magnesium || 0} target={400} unit="mg" />
          <NutrientRow label="IRON" icon="Fe" value={currentData.iron || 0} target={18} unit="mg" />
        </div>
      </div>
    </div>
  );
};

const Vial = ({ label, pct, color }: { label: string, pct: number, color: string }) => {
  const colorMap = {
    orange: 'from-[#f97316] to-[#c2410c]',
    red: 'from-[#ef4444] to-[#b91c1c]',
    cyan: 'from-[#22d3ee] to-[#0e7490]',
    green: 'from-[#4ade80] to-[#16a34a]',
    purple: 'from-[#a855f7] to-[#7e22ce]',
  };
  const glowMap = {
    orange: 'shadow-[0_0_10px_rgba(249,115,22,0.8)]',
    red: 'shadow-[0_0_10px_rgba(239,68,68,0.8)]',
    cyan: 'shadow-[0_0_10px_rgba(34,211,238,0.8)]',
    green: 'shadow-[0_0_10px_rgba(74,222,128,0.8)]',
    purple: 'shadow-[0_0_10px_rgba(168,85,247,0.8)]',
  };
  const textClass = {
    orange: 'text-[#f97316]',
    red: 'text-[#ef4444]',
    cyan: 'text-[#22d3ee]',
    green: 'text-[#4ade80]',
    purple: 'text-[#a855f7]',
  };

  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      {/* Tube Cap Top */}
      <div className="w-full max-w-[30px] h-3 bg-zinc-700 border-t border-l border-r border-zinc-500 rounded-t-sm" />
      {/* Tube Body */}
      <div className="h-[100px] sm:h-[140px] w-full max-w-[24px] bg-black/80 border-l border-r border-zinc-600 rounded-b-full relative overflow-hidden flex flex-col justify-end p-[2px]">
        {/* Glass Reflection */}
        <div className="absolute inset-y-0 left-[20%] w-[20%] bg-white/10 z-20 pointer-events-none" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-30 mix-blend-overlay z-10"></div>
        <motion.div 
          className={cn("w-full rounded-b-full bg-gradient-to-t relative", colorMap[color as keyof typeof colorMap], glowMap[color as keyof typeof glowMap])}
          initial={{ height: 0 }}
          animate={{ height: `${pct}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          {/* Bubbles */}
          <motion.div className="absolute top-1 left-[20%] w-1 h-1 bg-white/60 rounded-full" animate={{ y: [0, -30], opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 1.2 }} />
          <motion.div className="absolute top-4 right-[20%] w-1.5 h-1.5 bg-white/60 rounded-full" animate={{ y: [0, -20], opacity: [1, 0] }} transition={{ repeat: Infinity, duration: 1.5, delay: 0.5 }} />
        </motion.div>
      </div>
      {/* Tube Base */}
      <div className="w-full max-w-[30px] h-2 bg-zinc-800 border-b border-l border-r border-zinc-600 rounded-b-sm" />
      
      <div className={cn("stalker-font text-[10px] sm:text-xs uppercase mt-1", textClass[color as keyof typeof textClass])}>
        {label} {Math.round(pct)}%
      </div>
    </div>
  );
};

const NutrientRow = ({ label, icon, value, target, unit, divisor = 1, invert = false }: { label: string, icon: string, value: number, target: number, unit: string, divisor?: number, invert?: boolean }) => {
  const displayVal = (value / divisor);
  const displayTarget = (target / divisor);
  const pct = Math.min(100, Math.max(0, (displayVal / displayTarget) * 100));
  
  const isGoalMet = invert ? (displayVal <= displayTarget) : (displayVal >= displayTarget);
  // Default to false for UI simulation if target is 0 or undefined for micro nutrients
  const simulatedIsGoalMet = target > 0 ? isGoalMet : pct > 30;

  return (
    <div className="flex items-center gap-2 sm:gap-4 relative text-xs sm:text-sm">
      <div className="w-[80px] sm:w-[100px] flex items-center gap-2 stalker-font text-[#4ade80]">
        <div className="w-5 h-5 flex items-center justify-center border border-[#4ade80]/30 text-[10px]">
          {icon}
        </div>
        {label}
      </div>
      
      {/* Horizontal Tube */}
      <div className="flex-1 h-6 bg-black/80 border border-zinc-600 rounded-full relative overflow-hidden flex items-center px-1">
         {/* Glass Reflection */}
         <div className="absolute inset-x-0 top-[20%] h-[20%] bg-white/10 z-20 pointer-events-none rounded-full" />
         
         <div className="w-full h-4 bg-zinc-900 rounded-full overflow-hidden relative">
            <div 
              className={cn("h-full relative shadow-[0_0_10px_rgba(74,222,128,0.5)] solid-bg", simulatedIsGoalMet ? "bg-[#4ade80]" : "bg-red-600 shadow-[0_0_10px_rgba(220,38,38,0.5)]")}
              style={{ width: `${pct}%` }}
            >
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 mix-blend-overlay"></div>
            </div>
         </div>
      </div>
      
      <div className="w-[30px] sm:w-[40px] stalker-font text-[#4ade80]/80 text-right">
        {displayTarget > 0 ? displayTarget.toFixed(displayTarget % 1 === 0 ? 0 : 1) : '--'}{unit}
      </div>
      
      <div className="w-[80px] sm:w-[100px] flex justify-end">
        {simulatedIsGoalMet ? (
          <div className="flex items-center gap-1 text-[#4ade80] stalker-font text-[10px] sm:text-xs">
            CHARGE<br/>SECURED
            <div className="w-4 h-4 rounded-full border border-[#4ade80] flex items-center justify-center ml-1">
              <div className="w-2 h-2 bg-[#4ade80] rounded-full" />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-red-500 stalker-font text-[10px] sm:text-xs">
            LINK<br/>FAILURE
            <div className="w-4 h-4 rounded-full border border-red-500 flex items-center justify-center ml-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-3 h-3">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

